import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, type RouteObject } from 'react-router';
import { brand } from '@app/shared/brand';
import { useSession } from '../platform/session.tsx';
import { EmptyState, Spinner } from '../ui/index.ts';
import { AppShell } from './AppShell.tsx';
import { ALL_MODULES } from './modules.ts';
import { useModules } from './ModulesContext.tsx';
import type { WebModule } from './types.ts';

function Splash() {
  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', alignContent: 'center', gap: 'var(--space-4)' }}>
      <img src={brand.icons.svg} alt="" width={72} height={72} style={{ borderRadius: 20 }} />
      <Spinner label={`Opening ${brand.shortName}`} />
    </div>
  );
}

/** Auth screens: only when signed out. */
function PublicOnly() {
  const { status } = useSession();
  if (status === 'loading') return <Splash />;
  if (status === 'signed-in') return <Navigate to="/" replace />;
  return <Outlet />;
}

function RequireSignedIn({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();
  if (status === 'loading') return <Splash />;
  if (status === 'signed-out') return <Navigate to={`/welcome?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

/** The main app: signed in and part of at least one family. */
function RequireFamily() {
  const { me, activeFamily } = useSession();
  return <RequireSignedIn>{me && me.families.length === 0 ? <Navigate to="/onboarding" replace /> : activeFamily ? <AppShell /> : <Splash />}</RequireSignedIn>;
}

/** Routes of a module disabled on this server explain themselves instead of half-working. */
function ModuleGate({ id }: { id: WebModule['id'] }) {
  const enabled = useModules().some((m) => m.id === id);
  if (!enabled) return <EmptyState title="Not available">This feature is turned off for your family's server.</EmptyState>;
  return <Outlet />;
}

const lazyScreen = (load: () => Promise<{ Component: React.ComponentType }>) => ({ lazy: load });

export function createAppRouter() {
  const moduleRoutes: RouteObject[] = ALL_MODULES.map((m) => ({ element: <ModuleGate id={m.id} />, children: m.routes }));
  const routes: RouteObject[] = [
    {
      element: <PublicOnly />,
      children: [
        { path: 'welcome', ...lazyScreen(async () => ({ Component: (await import('../features/auth/screens.tsx')).WelcomeScreen })) },
        { path: 'signin', ...lazyScreen(async () => ({ Component: (await import('../features/auth/screens.tsx')).SignInScreen })) },
        { path: 'signup', ...lazyScreen(async () => ({ Component: (await import('../features/auth/screens.tsx')).SignUpScreen })) },
        { path: 'forgot', ...lazyScreen(async () => ({ Component: (await import('../features/auth/screens.tsx')).ForgotScreen })) },
        { path: 'reset-password', ...lazyScreen(async () => ({ Component: (await import('../features/auth/screens.tsx')).ResetPasswordScreen })) },
        { path: 'device', ...lazyScreen(async () => ({ Component: (await import('../features/auth/screens.tsx')).DeviceCodeScreen })) },
      ],
    },
    { path: 'join/:token', ...lazyScreen(async () => ({ Component: (await import('../features/auth/screens.tsx')).JoinScreen })) },
    {
      path: 'onboarding',
      element: (
        <RequireSignedIn>
          <Outlet />
        </RequireSignedIn>
      ),
      children: [{ index: true, ...lazyScreen(async () => ({ Component: (await import('../features/family/Onboarding.tsx')).OnboardingScreen })) }],
    },
    {
      path: '/',
      element: <RequireFamily />,
      children: [
        { index: true, ...lazyScreen(async () => ({ Component: (await import('../features/today/TodayScreen.tsx')).TodayScreen })) },
        ...moduleRoutes,
        { path: 'search', ...lazyScreen(async () => ({ Component: (await import('../features/search/SearchScreen.tsx')).SearchScreen })) },
        { path: 'inbox', ...lazyScreen(async () => ({ Component: (await import('../features/inbox/InboxScreen.tsx')).InboxScreen })) },
        { path: 'family', ...lazyScreen(async () => ({ Component: (await import('../features/family/FamilyScreen.tsx')).FamilyScreen })) },
        { path: 'more', ...lazyScreen(async () => ({ Component: (await import('../features/more/MoreScreen.tsx')).MoreScreen })) },
        {
          path: 'settings',
          children: [
            { index: true, ...lazyScreen(async () => ({ Component: (await import('../features/settings/SettingsScreens.tsx')).SettingsIndex })) },
            { path: 'profile', ...lazyScreen(async () => ({ Component: (await import('../features/settings/SettingsScreens.tsx')).ProfileSettings })) },
            { path: 'appearance', ...lazyScreen(async () => ({ Component: (await import('../features/settings/SettingsScreens.tsx')).AppearanceSettings })) },
            {
              path: 'notifications',
              ...lazyScreen(async () => ({ Component: (await import('../features/settings/SettingsScreens.tsx')).NotificationSettings })),
            },
            { path: 'security', ...lazyScreen(async () => ({ Component: (await import('../features/settings/SettingsScreens.tsx')).SecuritySettings })) },
            {
              path: 'integrations',
              ...lazyScreen(async () => ({ Component: (await import('../features/settings/SettingsScreens.tsx')).IntegrationsSettings })),
            },
            { path: 'data', ...lazyScreen(async () => ({ Component: (await import('../features/settings/SettingsScreens.tsx')).DataSettings })) },
            { path: 'account', ...lazyScreen(async () => ({ Component: (await import('../features/settings/SettingsScreens.tsx')).AccountSettings })) },
            { path: 'about', ...lazyScreen(async () => ({ Component: (await import('../features/settings/SettingsScreens.tsx')).AboutSettings })) },
            { path: 'dev-mailbox', ...lazyScreen(async () => ({ Component: (await import('../features/settings/SettingsScreens.tsx')).DevMailbox })) },
          ],
        },
        { path: '*', element: <EmptyState title="Page not found">That page doesn't exist.</EmptyState> },
      ],
    },
  ];
  // Shown while the first lazy screen loads.
  for (const r of routes) r.HydrateFallback = Splash;
  return createBrowserRouter(routes);
}
